import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { ExperienceLabel, LabelType } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { useLabelCache } from './useLabelCache';
import type {
  UpdateExperienceLabelSortOrderMutation,
  UpdateExperienceLabelSortOrderMutationVariables,
} from './useUpdateLabelSortOrder.generated';

const query = gql`
  mutation UpdateExperienceLabelSortOrder(
    $input: UpdateExperienceLabelSortOrderInput!
  ) {
    updateExperienceLabelSortOrder(input: $input)
  }
`;

export const useUpdateLabelSortOrder = (labelType: LabelType) => {
  const experience = useEffectiveExperience();
  const cache = useLabelCache();

  const { mutate, ...rest } = useMutation<
    UpdateExperienceLabelSortOrderMutation,
    QueryError,
    UpdateExperienceLabelSortOrderMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        UpdateExperienceLabelSortOrderMutation,
        UpdateExperienceLabelSortOrderMutationVariables
      >(query, variables),
  });

  const updateSortOrder = (
    labels: ExperienceLabel[],
    options?: MutateOptions<
      UpdateExperienceLabelSortOrderMutation,
      QueryError,
      UpdateExperienceLabelSortOrderMutationVariables
    >
  ) => {
    return mutate(
      {
        input: {
          experienceId: experience.id,
          labels: labels.map((label, index) => ({
            id: label.id,
            sortOrder: index,
          })),
        },
      },
      {
        ...options,
        onSuccess: (...args) => {
          cache.setSortOrder(labelType, labels);
          options?.onSuccess?.(...args);
        },
      }
    );
  };

  return { updateSortOrder, ...rest };
};
